/**
 * School Location Service
 * Handles API operations for school locations
 */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, tap, catchError, throwError } from 'rxjs';
import { APP_SCHEMA_ENDPOINTS } from '../api';
import { SchoolLocationRead } from '../models/school-location.types';

@Injectable({
  providedIn: 'root',
})
export class SchoolLocationService {
  private readonly http = inject(HttpClient);

  // Signals for reactive state management
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  /**
   * Get all school locations
   * @param params Optional query parameters (e.g., { deleted: false })
   */
  getSchoolLocations(params?: { deleted?: boolean }): Observable<SchoolLocationRead[]> {
    this.loading.set(true);
    this.error.set(null);

    const queryParams: any = {};
    if (params?.deleted !== undefined) {
      queryParams.deleted = params.deleted;
    }

    return this.http
      .get<SchoolLocationRead[]>(APP_SCHEMA_ENDPOINTS.schoolLocations.list, {
        params: queryParams,
        headers: this.createHeaders(),
      })
      .pipe(
        tap(() => {
          this.loading.set(false);
        }),
        catchError((error) => this.handleError(error)),
      );
  }

  /**
   * Get school location by ID
   */
  getSchoolLocation(locationId: number): Observable<SchoolLocationRead> {
    this.loading.set(true);
    this.error.set(null);

    return this.http
      .get<SchoolLocationRead>(APP_SCHEMA_ENDPOINTS.schoolLocations.getById(locationId), {
        headers: this.createHeaders(),
      })
      .pipe(
        tap(() => {
          this.loading.set(false);
        }),
        catchError((error) => this.handleError(error)),
      );
  }

  /**
   * Create authorization headers
   */
  private createHeaders(): HttpHeaders {
    const token = this.getAuthToken();
    return new HttpHeaders({
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    });
  }

  /**
   * Get auth token from localStorage
   */
  private getAuthToken(): string | null {
    if (typeof window === 'undefined') return null;
    return (
      localStorage.getItem('access_token') ||
      localStorage.getItem('accessToken') ||
      localStorage.getItem('auth_token')
    );
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: any): Observable<never> {
    this.loading.set(false);

    const errorMessage =
      error?.error?.message || error?.message || 'An error occurred while fetching data';

    this.error.set(errorMessage);
    console.error('SchoolLocationService error:', error);

    return throwError(() => new Error(errorMessage));
  }
}
